import { c as _c } from "react/compiler-runtime";
import { IcoArrowDown16, IcoArrowUp16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { Badge, IconButton, LinkButton } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import type { FieldKeyError } from '../../store/field-key';
import { useBuilder } from '../../store/provider';
import type { DecisionQuestion, DecisionQuestionPickOne, DecisionQuestionScore } from '../../store/types';
import { fieldRowId, uid } from '../../store/utils';
import FieldKeyErrorMessage from '../field-key-error';
import { ClickToReveal, InlineText, ProsePreview } from '../shared/inline-edit';
import MarkdownField from '../task/markdown-field';
type QuestionCardProps = {
  nodeId: string;
  question: DecisionQuestion;
  keyError?: FieldKeyError;
  canUp: boolean;
  canDown: boolean;
};
const MIN_OPTIONS = 2;
const MIN_LEVELS = 2;
const MAX_LEVELS = 10;
const kindLabel = (kind: DecisionQuestion['kind'], t: ReturnType<typeof useTranslation>['t']) => {
  if (kind === 'yes_no') return t($ => $.yesNo);
  if (kind === 'pick_one') return t($ => $.pickOne);
  return t($ => $.score);
};
const QuestionBody = t0 => {
  const $ = _c(6);
  const {
    nodeId,
    question
  } = t0;
  if (question.kind === "pick_one") {
    let t1;
    if ($[0] !== nodeId || $[1] !== question) {
      t1 = <OptionsEditor nodeId={nodeId} question={question} />;
      $[0] = nodeId;
      $[1] = question;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (question.kind === "score") {
    let t1;
    if ($[3] !== nodeId || $[4] !== question) {
      t1 = <LevelsEditor nodeId={nodeId} question={question} />;
      $[3] = nodeId;
      $[4] = question;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  return null;
};
const LevelsEditor = t0 => {
  const $ = _c(36);
  const {
    nodeId,
    question
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision.question.levels"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="text-body-3 text-tertiary">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== actions || $[6] !== canEdit || $[7] !== nodeId || $[8] !== question.id || $[9] !== question.levels || $[10] !== t) {
    let t5;
    if ($[12] !== actions || $[13] !== canEdit || $[14] !== nodeId || $[15] !== question.id || $[16] !== question.levels.length || $[17] !== t) {
      t5 = (level, index) => <div key={level.id} className="flex items-center gap-2"><span className="w-6 shrink-0 text-snippet-2 text-tertiary">{index}</span><InlineText value={level.description} wrap={true} ariaLabel={t(_temp2)} placeholder={t(_temp3)} canEdit={canEdit} hasError={!level.description.trim()} onChange={description => actions.decision.updateLevel(nodeId, question.id, level.id, description)} />{canEdit && question.levels.length > MIN_LEVELS && <IconButton size="tiny" aria-label={t(_temp4)} icon={IcoTrash16} destructive={true} onClick={() => actions.decision.removeLevel(nodeId, question.id, level.id)} />}</div>;
      $[12] = actions;
      $[13] = canEdit;
      $[14] = nodeId;
      $[15] = question.id;
      $[16] = question.levels.length;
      $[17] = t;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    t4 = question.levels.map(t5);
    $[5] = actions;
    $[6] = canEdit;
    $[7] = nodeId;
    $[8] = question.id;
    $[9] = question.levels;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = question.levels.length - 1;
  let t6;
  if ($[19] !== t || $[20] !== t5) {
    t6 = t(_temp5, {
      max: t5
    });
    $[19] = t;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t6) {
    t7 = <span className="text-caption-3 text-tertiary">{t6}</span>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== actions || $[25] !== canEdit || $[26] !== nodeId || $[27] !== question.id || $[28] !== question.levels.length || $[29] !== t) {
    t8 = canEdit && question.levels.length < MAX_LEVELS && <div className="mt-1 self-start"><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => actions.decision.addLevel(nodeId, question.id, uid("lvl"))}>{t(_temp6)}</LinkButton></div>;
    $[24] = actions;
    $[25] = canEdit;
    $[26] = nodeId;
    $[27] = question.id;
    $[28] = question.levels.length;
    $[29] = t;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t3 || $[32] !== t4 || $[33] !== t7 || $[34] !== t8) {
    t9 = <div className="flex flex-col gap-1">{t3}{t4}{t7}{t8}</div>;
    $[31] = t3;
    $[32] = t4;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
const OptionsEditor = t0 => {
  const $ = _c(29);
  const {
    nodeId,
    question
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision.question.options"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp7);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="text-body-3 text-tertiary">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== actions || $[6] !== canEdit || $[7] !== nodeId || $[8] !== question.id || $[9] !== question.options || $[10] !== t) {
    let t5;
    if ($[12] !== actions || $[13] !== canEdit || $[14] !== nodeId || $[15] !== question.id || $[16] !== question.options.length || $[17] !== t) {
      t5 = option => <div key={option.id} className="flex items-center gap-2"><div className="w-48 shrink-0"><InlineText value={option.value} mono={true} ariaLabel={t(_temp8)} placeholder={t(_temp9)} canEdit={canEdit} hasError={!option.value.trim()} onChange={value => actions.decision.updateOption(nodeId, question.id, option.id, {
            value
          })} /></div><InlineText value={option.description} muted={true} wrap={true} ariaLabel={t(_temp10)} placeholder={t(_temp11)} canEdit={canEdit} onChange={description => actions.decision.updateOption(nodeId, question.id, option.id, {
          description
        })} />{canEdit && question.options.length > MIN_OPTIONS && <IconButton size="tiny" aria-label={t(_temp12)} icon={IcoTrash16} destructive={true} onClick={() => actions.decision.removeOption(nodeId, question.id, option.id)} />}</div>;
      $[12] = actions;
      $[13] = canEdit;
      $[14] = nodeId;
      $[15] = question.id;
      $[16] = question.options.length;
      $[17] = t;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    t4 = question.options.map(t5);
    $[5] = actions;
    $[6] = canEdit;
    $[7] = nodeId;
    $[8] = question.id;
    $[9] = question.options;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[19] !== actions || $[20] !== canEdit || $[21] !== nodeId || $[22] !== question.id || $[23] !== t) {
    t5 = canEdit && <div className="mt-1 self-start"><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => actions.decision.addOption(nodeId, question.id, uid("opt"))}>{t(_temp13)}</LinkButton></div>;
    $[19] = actions;
    $[20] = canEdit;
    $[21] = nodeId;
    $[22] = question.id;
    $[23] = t;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== t3 || $[26] !== t4 || $[27] !== t5) {
    t6 = <div className="flex flex-col gap-1">{t3}{t4}{t5}</div>;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
const QuestionCard = t0 => {
  const $ = _c(69);
  const {
    nodeId,
    question,
    keyError,
    canUp,
    canDown
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision.question"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  let t2;
  if ($[1] !== actions.decision || $[2] !== nodeId || $[3] !== question.id) {
    t2 = patch => actions.decision.updateQuestion(nodeId, question.id, patch);
    $[1] = actions.decision;
    $[2] = nodeId;
    $[3] = question.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const update = t2;
  let t3;
  if ($[5] !== question.id) {
    t3 = fieldRowId(question.id);
    $[5] = question.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== question.kind || $[8] !== t) {
    t4 = kindLabel(question.kind, t);
    $[7] = question.kind;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <Badge variant="neutral">{t4}</Badge>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = question.key;
  let t7;
  if ($[12] !== t) {
    t7 = t(_temp14);
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t(_temp15);
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = !!keyError;
  let t10;
  if ($[16] !== update) {
    t10 = key => update({
      key
    });
    $[16] = update;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== canEdit || $[19] !== question.key || $[20] !== t10 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = <InlineText value={t6} prefix="outputs." mono={true} ariaLabel={t7} placeholder={t8} canEdit={canEdit} hasError={t9} onChange={t10} />;
    $[18] = canEdit;
    $[19] = question.key;
    $[20] = t10;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== actions.decision || $[26] !== canDown || $[27] !== canEdit || $[28] !== canUp || $[29] !== nodeId || $[30] !== question.id || $[31] !== t) {
    t12 = canEdit && <div className="ml-auto flex items-center gap-0.5">{canUp && <IconButton size="tiny" aria-label={t(_temp16)} icon={IcoArrowUp16} onClick={() => actions.decision.moveQuestion(nodeId, question.id, "up")} />}{canDown && <IconButton size="tiny" aria-label={t(_temp17)} icon={IcoArrowDown16} onClick={() => actions.decision.moveQuestion(nodeId, question.id, "down")} />}<IconButton size="tiny" aria-label={t(_temp18)} icon={IcoTrash16} destructive={true} onClick={() => actions.decision.removeQuestion(nodeId, question.id)} /></div>;
    $[25] = actions.decision;
    $[26] = canDown;
    $[27] = canEdit;
    $[28] = canUp;
    $[29] = nodeId;
    $[30] = question.id;
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t5) {
    t13 = <div className="flex items-center gap-2">{t5}{t11}{t12}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t5;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== keyError) {
    t14 = <FieldKeyErrorMessage error={keyError} />;
    $[37] = keyError;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t) {
    t15 = t(_temp19);
    $[39] = t;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const t16 = question.prompt;
  let t17;
  if ($[41] !== t) {
    t17 = t(_temp20);
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== canEdit || $[44] !== question.prompt || $[45] !== t17) {
    t18 = <ProsePreview value={t16} placeholder={t17} canEdit={canEdit} />;
    $[43] = canEdit;
    $[44] = question.prompt;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== nodeId || $[48] !== question.prompt || $[49] !== t || $[50] !== update) {
    t19 = () => <MarkdownField label={t(_temp21)} hint={t(_temp22)} value={question.prompt} onChange={prompt => update({
      prompt
    })} mentionNodeId={nodeId} />;
    $[47] = nodeId;
    $[48] = question.prompt;
    $[49] = t;
    $[50] = update;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== canEdit || $[53] !== t15 || $[54] !== t18 || $[55] !== t19) {
    t20 = <ClickToReveal canEdit={canEdit} ariaLabel={t15} className="px-1 py-0.5" display={t18}>{t19}</ClickToReveal>;
    $[52] = canEdit;
    $[53] = t15;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== canEdit || $[58] !== nodeId || $[59] !== question || $[60] !== t || $[61] !== update) {
    t21 = question.kind === "yes_no" ? <><div className="flex items-center gap-2"><span className="shrink-0 text-body-3 text-tertiary">{t(_temp23)}</span><InlineText value={question.yesMeans} muted={true} wrap={true} ariaLabel={t(_temp24)} placeholder={t(_temp25)} canEdit={canEdit} onChange={yesMeans => update({
          yesMeans
        })} /></div><div className="flex items-center gap-2"><span className="shrink-0 text-body-3 text-tertiary">{t(_temp26)}</span><InlineText value={question.noMeans} muted={true} wrap={true} ariaLabel={t(_temp27)} placeholder={t(_temp28)} canEdit={canEdit} onChange={noMeans => update({
          noMeans
        })} /></div></> : <QuestionBody nodeId={nodeId} question={question} />;
    $[57] = canEdit;
    $[58] = nodeId;
    $[59] = question;
    $[60] = t;
    $[61] = update;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t13 || $[64] !== t14 || $[65] !== t20 || $[66] !== t21 || $[67] !== t3) {
    t22 = <div data-id={t3} className="flex flex-col gap-2 rounded border border-tertiary px-2 py-2">{t13}{t14}{t20}{t21}</div>;
    $[63] = t13;
    $[64] = t14;
    $[65] = t20;
    $[66] = t21;
    $[67] = t3;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  return t22;
};
export default QuestionCard;
function _temp($) {
  return $.label;
}
function _temp2($_0) {
  return $_0.descriptionAria;
}
function _temp3($_1) {
  return $_1.descriptionPlaceholder;
}
function _temp4($_2) {
  return $_2.removeAria;
}
function _temp5($_3) {
  return $_3.hint;
}
function _temp6($_4) {
  return $_4.add;
}
function _temp7($) {
  return $.label;
}
function _temp8($_0) {
  return $_0.valueAria;
}
function _temp9($_1) {
  return $_1.valuePlaceholder;
}
function _temp10($_2) {
  return $_2.descriptionAria;
}
function _temp11($_3) {
  return $_3.descriptionPlaceholder;
}
function _temp12($_4) {
  return $_4.removeAria;
}
function _temp13($_5) {
  return $_5.add;
}
function _temp14($) {
  return $.keyAria;
}
function _temp15($_0) {
  return $_0.keyPlaceholder;
}
function _temp16($_1) {
  return $_1.moveUp;
}
function _temp17($_2) {
  return $_2.moveDown;
}
function _temp18($_3) {
  return $_3.remove;
}
function _temp19($_4) {
  return $_4.prompt.ariaLabel;
}
function _temp20($_5) {
  return $_5.prompt.placeholder;
}
function _temp21($_6) {
  return $_6.prompt.label;
}
function _temp22($_7) {
  return $_7.prompt.hint;
}
function _temp23($_8) {
  return $_8.yesMeans.label;
}
function _temp24($_9) {
  return $_9.yesMeans.label;
}
function _temp25($_10) {
  return $_10.yesMeans.placeholder;
}
function _temp26($_11) {
  return $_11.noMeans.label;
}
function _temp27($_12) {
  return $_12.noMeans.label;
}
function _temp28($_13) {
  return $_13.noMeans.placeholder;
}